import { atom } from "jotai";
import { darkThemeClass } from "../../../ui/styles/theme.css";
import { lightThemeConfig } from "../../../ui/styles/lightTheme";
import { darkThemeConfig } from "../../../ui/styles/darkTheme";

export type ThemeMode = "light" | "dark";

function isAccountAppHostRuntime(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname, port } = window.location;
  if (/^account(-dev)?\.lionenergy\.com$/.test(hostname)) return true;
  if (hostname === "account.localhost") return true;
  if ((hostname === "localhost" || hostname === "127.0.0.1") && port === "5174") {
    return true;
  }
  return false;
}

// Get initial dark mode state
export const getInitialDarkMode = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    // Check localStorage for saved preference
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) {
      return saved === "true";
    }
  } catch {
    // Firefox can throw when storage is blocked
  }
  // Default to system preference
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
};

/** Apply theme class + shell colors to the document (stable across VE hashed var names). */
export function applyThemeToDocument(isDark: boolean) {
  const mode: ThemeMode = isDark ? "dark" : "light";
  const html = document.documentElement;
  const body = document.body;
  const root = document.getElementById("root");
  const tokens = isDark ? darkThemeConfig.color : lightThemeConfig.color;
  // Account auth pages use surfaceAuth; keep the document shell matched so
  // light mode doesn't flash white (#ffffff) under the warm auth surface.
  const bg =
    isAccountAppHostRuntime() && !isDark ? tokens.surfaceAuth : tokens.bg;
  const fg = tokens.fg;

  html.dataset.theme = mode;
  html.classList.toggle(darkThemeClass, isDark);
  body.classList.toggle(darkThemeClass, isDark);
  root?.classList.toggle(darkThemeClass, isDark);

  // Belt-and-suspenders: literal shell colors win over any VE globalStyle timing/HMR issues.
  for (const el of [html, body, root]) {
    if (!el) continue;
    el.style.setProperty("background-color", bg, "important");
    el.style.setProperty("color", fg, "important");
  }
}

// Atom to track dark mode state
export const isDarkModeAtom = atom<boolean>(getInitialDarkMode());

// Derived atom to apply dark mode class to html element
export const applyDarkModeAtom = atom(
  null,
  (_get, set, isDark: boolean) => {
    set(isDarkModeAtom, isDark);
    localStorage.setItem("darkMode", String(isDark));
    applyThemeToDocument(isDark);
  },
);

// Initialized from main.tsx after theme.css is loaded — do not auto-run here.
